import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { size } from 'lodash';
import listCurrency from '../../../constantsApp/currency.json';
import SubForm from './SubForm';
import TierForm from './TierForm';
import { GenericInput, FormCollapse } from '../../common';

const currencyOptions = listCurrency.currencies.map(item => ({
  value: item.code,
  label: `${item.name} (${item.code})`,
}));

const tierInputs = [
  {
    label: 'label.startRange',
    wrapperClass: 'col-md-4',
    name: 'minimum',
    type: 'number',
    required: true,
  },
  {
    label: 'label.endRange',
    wrapperClass: 'col-md-4',
    name: 'maximum',
    type: 'number',
    required: true,
  },
];

const renderTier = ({
  t,
  item,
  index,
  onRemoveTier,
  isComplexUsage,
  creatingMode,
  priceRemovable,
  optionsAccumulatorId,
  optionsGrantId,
  grantInputs,
  accumulatorInputs,
  isNotTier,
  onToggleTab,
  activeTab,
  transactionType,
  pricingModel,
}) => {
  return (
    item.tiers &&
    item.tiers
      .filter(v => size(v) > 1)
      .map((tier, idx) => {
        let title = '-';
        if (tier.minimum && tier.minimum !== '0') {
          title = `<${tier.minimum}>${title}`;
        } else {
          title = `<0>${title}`;
        }
        if (tier.maximum && tier.maximum !== '0') {
          title = `${title}<${tier.maximum}>`;
        } else {
          title = `${title}<0>`;
        }
        return (
          <div className={!isNotTier ? 'card-body' : ''} key={`tier-${index}-${idx}`}>
            <FormCollapse
              key={`tier-${idx}`}
              label="First Name"
              isActive={activeTab && `tier-${index}-${idx}` === activeTab.name && activeTab.isActive}
              title={title}
              state={`tier-${index}-${idx}`}
              onToggleTab={onToggleTab}
            >
              {!isNotTier && (
                <div className="card-header">
                  {`${t('label.addTier')}#`}
                  {idx + 1}
                  {item.tiers.length > 1 && (
                    <button type="button" className="btn-rm-card" onClick={() => onRemoveTier({ idx, index })}>
                      <i className="fa fa-trash" />
                    </button>
                  )}
                </div>
              )}
              <SubForm
                headerTitle={`#${idx + 1}`}
                inputs={
                  !isComplexUsage
                    ? tierInputs
                    : tierInputs.map(val => {
                        const { required, ...rest } = val;
                        return rest;
                      })
                }
                onRemoveItem={e => {
                  e.preventDefault();
                }}
                item={tier}
                isRemoveable={creatingMode && item.tiers.length > 1 && priceRemovable}
                isComplexUsage={isComplexUsage}
                index={index}
              />
              <TierForm
                data={tier}
                isTier
                optionsAccumulatorId={optionsAccumulatorId}
                optionsGrantId={optionsGrantId}
                grantInputs={grantInputs}
                accumulatorInputs={accumulatorInputs}
                isComplexUsage={isComplexUsage}
                index={index}
                transactionType={transactionType}
                pricingModel={pricingModel}
              />
            </FormCollapse>
          </div>
        );
      })
  );
};

const PricesForm = ({
  item,
  index,
  priceRemovable,
  creatingMode,
  optionsAccumulatorId,
  optionsGrantId,
  pricingModel,
  isComplexUsage,
  readOnlyCurrency,
  selectField,
  transactionType,
}) => {
  const [activeTab, setActiveTab] = useState({});

  const onToggleTab = activeTabName => {
    if (activeTab.name === activeTabName) {
      setActiveTab({ name: activeTabName, isActive: !activeTab.isActive });
    } else {
      setActiveTab({ name: activeTabName, isActive: true });
    }
  };
  const priceInputs = [
    {
      label: 'common:label.currency',
      wrapperClass: 'col-md-4',
      name: 'currencyId',
      type: 'select',
      options: currencyOptions,
      disabled: index > 1 && !isComplexUsage,
      readOnly: readOnlyCurrency,
      menuPortalTarget: true,
    },
    {
      label: 'common:label.amount',
      wrapperClass: 'col-md-4',
      name: 'amount',
      type: 'number',
      required: true,
      isNegativeValue: true,
    },
  ];

  const { t } = useTranslation('common');
  if (!item.prices && !item.tiers && !item.accumulators && !item.grants) return '';

  const grantInputs = [
    {
      label: 'label.grantId',
      wrapperClass: 'col-md-4',
      name: 'resourceId',
      type: 'select',
      required: true,
      options: optionsGrantId,
      menuPortalTarget: true,
    },
    {
      label: 'label.amount',
      wrapperClass: 'col-md-4',
      name: 'amount',
      type: 'number',
      required: true,
      isNegativeValue: true,
    },
    {
      label: 'label.validityUnit',
      wrapperClass: 'col-md-4',
      name: 'grantUnit',
      type: 'select',
      required: true,
      tOptions: 'selections:grantUnit',
      menuPortalTarget: true,
    },
    {
      label: 'label.validityDuration',
      wrapperClass: 'col-md-4',
      name: 'grantDuration',
      type: 'number',
      required: true,
    },
  ];

  const accumulatorInputs = [
    {
      label: 'label.accumulatorId',
      wrapperClass: 'col-md-4',
      name: 'accumulatorId',
      type: 'select',
      required: true,
      options: optionsAccumulatorId,
      isClearable: true,
      menuPortalTarget: true,
    },
    {
      label: 'label.amount',
      wrapperClass: 'col-md-4',
      name: 'amount',
      type: 'number',
      required: true,
      isNegativeValue: true,
    },
    {
      label: 'label.validityUnit',
      wrapperClass: 'col-md-4',
      name: 'unit',
      type: 'select',
      required: true,
      tOptions: 'selections:accumulatorUnit',
      menuPortalTarget: true,
    },
    {
      label: 'label.validityDuration',
      wrapperClass: 'col-md-4',
      name: 'duration',
      type: 'number',
      required: true,
    },
  ];
  if (pricingModel === 'TRANSACTION_ATTRIBUTE_BASED') {
    return (
      <div className="card price-panel">
        <TierForm
          data={item}
          onToggleTab={onToggleTab}
          optionsAccumulatorId={optionsAccumulatorId}
          optionsGrantId={optionsGrantId}
          grantInputs={grantInputs}
          accumulatorInputs={accumulatorInputs}
          activeTab={activeTab}
          pricingModel={pricingModel}
          transactionType={transactionType}
        />
      </div>
    );
  }

  const isComplexWithTier = isComplexUsage && selectField.indexOf(4) > -1;

  return (
    <div>
      <div className="card price-panel">
        {pricingModel !== 'FLAT' && (
          <div className="card-header">
            {t('label.price')}
#
            {index + 1}
          </div>
        )}

        <div className="card-body">
          {item &&
            item.prices &&
            item.prices.map((price, idx) => {
              return (
                <div className="card-body" key={`prices-${idx}`}>
                  {pricingModel !== 'FLAT' && (
                    <div className="card-header">
                      {`${t('label.price')}#`}
                      {index + 1}
                    </div>
                  )}
                  <SubForm
                    key={`${index}-${idx}-sub-form`}
                    headerTitle={`#${idx + 1}`}
                    inputs={priceInputs}
                    onRemoveItem={e => {
                      e.preventDefault();
                    }}
                    item={price}
                    onChangeItem={() => {}}
                    isComplexUsage={isComplexUsage}
                    index={index}
                  />
                </div>
              );
            })}
          {item &&
            item.grants &&
            item.grants.length > 0 &&
            item.grants.map((grant, idx) => {
              if (grant && size(grant) > 1) {
                return (
                  <div key={`grant-${idx}`}>
                    <div className="card-header">{`Grant#${idx + 1}`}</div>
                    <div className="row col-md-12 p-3">
                      {grantInputs.map(item => (
                        <GenericInput
                          key={`grants-${item.name}`}
                          {...item}
                          value={grant[item.name]}
                          readOnly
                          disabled
                        />
                      ))}
                    </div>
                  </div>
                );
              }
              return '';
            })}
          {item &&
            item.accumulators &&
            item.accumulators.length > 0 &&
            item.accumulators.map((accumulator, idx) => {
              if (accumulator && size(accumulator) > 2) {
                return (
                  <div key={`accumulators-${idx}`}>
                    <div className="card-header">{`Accumulator#${idx + 1}`}</div>
                    <div className="row col-md-12 p-3" key={`grant-${idx}`}>
                      {accumulatorInputs.map(item => (
                        <GenericInput
                          key={`accumulators-${item.name}`}
                          {...item}
                          value={accumulator[item.name]}
                          readOnly
                          disabled
                        />
                      ))}
                    </div>
                  </div>
                );
              }
              return '';
            })}
          {(!isComplexUsage || isComplexWithTier) &&
            renderTier({
              item,
              index,
              isComplexUsage,
              creatingMode,
              priceRemovable,
              optionsAccumulatorId,
              optionsGrantId,
              grantInputs,
              accumulatorInputs,
              onToggleTab,
              activeTab,
              t,
              transactionType,
              pricingModel,
            })}
        </div>
      </div>
      <div>
        {isComplexUsage && !isComplexWithTier && (
          <div>
            {renderTier({
              item,
              index,
              isComplexUsage,
              creatingMode,
              priceRemovable,
              optionsAccumulatorId,
              optionsGrantId,
              grantInputs,
              accumulatorInputs,
              isNotTier: true,
              onToggleTab,
              t,
              activeTab,
            })}
          </div>
        )}
      </div>
    </div>
  );
};

PricesForm.propTypes = {
  item: PropTypes.objectOf(PropTypes.any),
  index: PropTypes.number.isRequired,
  priceRemovable: PropTypes.bool,
  creatingMode: PropTypes.bool,
  optionsAccumulatorId: PropTypes.arrayOf(PropTypes.any),
  optionsGrantId: PropTypes.arrayOf(PropTypes.any),
  selectField: PropTypes.arrayOf(PropTypes.any),
  pricingModel: PropTypes.string,
  transactionType: PropTypes.string,
  isComplexUsage: PropTypes.bool,
  readOnlyCurrency: PropTypes.bool,
};

PricesForm.defaultProps = {
  item: {},
  priceRemovable: true,
  creatingMode: true,
  optionsAccumulatorId: [],
  optionsGrantId: [],
  selectField: [],
  pricingModel: '',
  transactionType: '',
  isComplexUsage: false,
  readOnlyCurrency: false,
};
export default PricesForm;
